import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { BuildingsIcon } from '@phosphor-icons/react';

import { ConfirmationModal } from '../../../components/ConfirmationModal/ConfirmationModal';

interface ProfileCompletionModalProps {
  open: boolean;
  /** "Skip / Close" — closes the popup; caller surfaces the reduced-accuracy warning. */
  onSkip: () => void;
  /** "Take me to Organization Hub" — navigates to the profile hub. */
  onComplete: () => void;
}

/**
 * One-time popup shown on the New Assessment page when the Organization Profile
 * has zero fields filled, nudging the user to complete it before assessing.
 * Presentational — the page owns when to open it and what happens on skip.
 */
export const ProfileCompletionModal: FC<ProfileCompletionModalProps> = observer(({
  open,
  onSkip,
  onComplete,
}) => {
  const { t } = useTrans();

  return (
    <ConfirmationModal
      open={open}
      onClose={onSkip}
      title={t('app.orgProfile.nudge.modal.title')}
      icon={<BuildingsIcon size={28} />}
      iconColor="primary"
      content={
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {t('app.orgProfile.nudge.modal.body')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.tertiary' }}>
            {t('app.orgProfile.nudge.modal.subBody')}
          </Typography>
        </Box>
      }
      buttons={[
        {
          label: t('app.orgProfile.nudge.modal.skip'),
          onClick: onSkip,
          variant: 'outlined',
        },
        {
          label: t('app.orgProfile.nudge.modal.complete'),
          onClick: onComplete,
          color: 'primary',
          startIcon: <BuildingsIcon size={16} />,
        },
      ]}
    />
  );
});
